
"use client";

import { useEffect, useMemo, useRef, useState, useCallback, useTransition, useDeferredValue } from "react";
import type { ArtItem, Artist } from "@/types";
import { ArtCard } from "@/components/art-card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2 } from "lucide-react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  DocumentData,
  QueryConstraint,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

type ArtworkWithArtists = ArtItem & { artists: Artist[] };
const PAGE_SIZE = 40;

// --- small utilities ---
const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Debounce with state — no extra deps
function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Fetch artists by ids (prefer) or names (fallback)
async function fetchArtistsByIdsOrNames(
  ids: string[] | undefined,
  names: string[] | undefined
): Promise<Map<string, Artist>> {
  const artistsCol = collection(db, "artists");
  const result = new Map<string, Artist>();

  if (ids && ids.length) {
    for (const group of chunk(ids, 10)) {
      const snaps = await getDocs(query(artistsCol, where("__name__", "in", group)));
      snaps.forEach((d) => result.set(d.id, { ...(d.data() as Artist), id: d.id }));
    }
    return result;
  }

  if (names && names.length) {
    for (const group of chunk(names, 10)) {
      const snaps = await getDocs(query(artistsCol, where("name", "in", group)));
      snaps.forEach((d) => {
        const data = d.data() as Artist;
        result.set(data.name, { ...data, id: d.id });
      });
    }
  }
  return result;
}

function toArtworkWithArtists(
  docSnap: DocumentSnapshot<DocumentData>,
  artistsMapById: Map<string, Artist>,
  artistsMapByName: Map<string, Artist>
): ArtworkWithArtists {
  const data = docSnap.data() as any;

  const artistIds: string[] | undefined = data.artistIds;
  const artistNames: string[] = Array.isArray(data.artistNames) ? data.artistNames : [];

  const artists: Artist[] = artistIds?.length
    ? artistIds.map((id) => artistsMapById.get(id)).filter(Boolean) as Artist[]
    : artistNames.map((name) => artistsMapByName.get(name)).filter(Boolean) as Artist[];

  return {
    id: docSnap.id,
    title: data.title ?? "",
    artistNames,
    artists,
    year: data.year,
    description: data.description ?? "",
    price: typeof data.price === "number" ? data.price : 0,
    currency: data.currency ?? "USD",
    imageUrls: data.imageUrls || [data.imageUrl || "https://placehold.co/600x800.png"],
    imageAssets: data.imageAssets,
    imageMetadata: data.imageMetadata,
    searchTerms: data.searchTerms ?? [],
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? new Date().toISOString(),
    status: data.status,
    visibility: data.visibility,
  } as ArtworkWithArtists;
}


async function fetchAllArtworks(): Promise<ArtworkWithArtists[]> {
    const artworksCol = collection(db, 'artworks');
    const q = query(artworksCol, where("visibility", "==", "public"));
    const snaps = await getDocs(q);

    if (snaps.empty) {
        return [];
    }

    const pageArtistIds = new Set<string>();
    const pageArtistNames = new Set<string>();
    snaps.forEach((s) => {
      const d = s.data() as any;
      (d.artistIds ?? []).forEach((id: string) => pageArtistIds.add(id));
      (d.artistNames ?? []).forEach((n: string) => pageArtistNames.add(n));
    });

    const artistsById = await fetchArtistsByIdsOrNames(Array.from(pageArtistIds), undefined);
    const artistsByName = pageArtistIds.size ? new Map<string, Artist>() : artistsById;

    return snaps.docs.map((d) => toArtworkWithArtists(d, artistsById, artistsByName));
}

export function DiscoverPage({ initialItems = [] }: { initialItems?: ArtworkWithArtists[] }) {
  const [allItems, setAllItems] = useState<ArtworkWithArtists[]>(initialItems);
  const [displayedItems, setDisplayedItems] = useState<ArtworkWithArtists[]>([]);
  const [loading, setLoading] = useState(initialItems.length === 0);
  const [hasMore, setHasMore] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 350);

  const [sortOption, setSortOption] = useState("createdAt-desc");
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (initialItems.length > 0) {
        return;
    }
    setLoading(true);
    fetchAllArtworks().then(items => {
        setAllItems(items);
        setLoading(false);
    });
  }, [initialItems.length]);

   const filteredAndSortedItems = useMemo(() => {
        let items = [...allItems];

        if (debouncedSearch) {
            const lowercasedTerm = debouncedSearch.toLowerCase();
            items = items.filter(item => 
                item.title.toLowerCase().includes(lowercasedTerm) ||
                item.artistNames.some(name => name.toLowerCase().includes(lowercasedTerm)) ||
                (item.artists && item.artists.some(artist => artist.aliases?.some(alias => alias.toLowerCase().includes(lowercasedTerm)))) ||
                item.searchTerms.some(tag => tag.toLowerCase().includes(lowercasedTerm))
            );
        }

        items.sort((a, b) => {
            switch (sortOption) {
                case "price-asc":
                    return (a.price || 0) - (b.price || 0);
                case "price-desc":
                    return (b.price || 0) - (a.price || 0);
                case "createdAt-asc":
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                case "createdAt-desc":
                default:
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            }
        });

        return items;
    }, [allItems, debouncedSearch, sortOption]);
    
    useEffect(() => {
        setDisplayedItems(filteredAndSortedItems.slice(0, PAGE_SIZE));
        setHasMore(filteredAndSortedItems.length > PAGE_SIZE);
    }, [filteredAndSortedItems]);

    const handleNextPage = () => {
        const currentLength = displayedItems.length;
        const nextItems = filteredAndSortedItems.slice(currentLength, currentLength + PAGE_SIZE);
        setDisplayedItems(prev => [...prev, ...nextItems]);
        setHasMore(filteredAndSortedItems.length > currentLength + PAGE_SIZE);
    };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h2 className="text-3xl font-headline text-center mb-2">Discover Artworks</h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto">
          Explore a diverse collection of public artworks from talented artists around the world.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-1">
          <Label htmlFor="search-discover" className="sr-only">
            Search
          </Label>
          <Input
            id="search-discover"
            name="search-discover"
            placeholder="Search by keyword or tag..."
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              startTransition(() => setSearchTerm(v));
            }}
            className="w-full"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Label htmlFor="sort-discover" className="text-sm font-medium text-muted-foreground">
            Sort by
          </Label>
          <Select
            value={sortOption}
            onValueChange={(value) => {
              startTransition(() => setSortOption(value));
            }}
          >
            <SelectTrigger id="sort-discover" className="w-[180px]" aria-label="Sort by">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt-desc">Newest</SelectItem>
              <SelectItem value="createdAt-asc">Oldest</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : displayedItems.length > 0 ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {displayedItems.map((item, index) => (
              <ArtCard key={item.id} item={item} priority={index < 4} showPrice={false}/>
            ))}
          </div>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
             Showing {displayedItems.length} of {filteredAndSortedItems.length} items
             {!hasMore && " • End of results"}
          </div>
          
          {hasMore && (
              <div className="mt-8 text-center">
                  <Button onClick={handleNextPage}>
                      Load More
                  </Button>
              </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold">No Artwork Found</h3>
          <p className="text-muted-foreground mt-2">Try a different keyword or sort.</p>
        </div>
      )}
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="flex flex-col space-y-3">
      <Skeleton className="h-[350px] w-full rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    </div>
  );
}
